// Client Portal — Patient Aftercare portal. Extracted from App.js.
import { useState, useEffect, useRef } from "react";
import {
  BarChart3,
  Bell,
  FileText,
  Heart,
  Key,
  Loader2,
  Palette,
  Send,
  ShieldCheck,
  Sliders,
  Users,
} from "lucide-react";
import { authFetch } from "../lib/api";
import { KBPanel, BriefingsPanel, KB_ROLE_LEVELS } from "./RAGChat";
import { RAGApiKeys } from "./RAGApiKeys";
import { RAGAuditLog } from "./RAGAuditLog";
import { RAGCustomize } from "./RAGCustomize";
import { RAGPromptStudio } from "./RAGPromptStudio";
import { UserManagement } from "../components/UserManagement";
import { SystemBanner } from "../components/SystemBanner";
import { UsageAnalyticsDashboard } from "../analytics/UsageAnalyticsDashboard";

const AftercareChatPanel = ({ facility, user }) => {
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const fid = facility.facility_id;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const systemContext =
    "You are a patient aftercare assistant. Help patients and caregivers understand discharge instructions, medication schedules, what warning signs to watch for, and when to seek follow-up care. Use clear, plain language. Always recommend contacting the care team for unusual symptoms.";

  const submit = async () => {
    const text = query.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setQuery("");
    setLoading(true);
    try {
      const res = await authFetch(
        `/rag/chat?facility_id=${encodeURIComponent(fid)}`,
        {
          method: "POST",
          body: JSON.stringify({
            message: text,
            system_context: systemContext,
          }),
        },
      );
      const reply = res?.reply || res?.answer || "No response received.";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "Failed to get a response. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-[600px]">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Heart className="w-4 h-4 text-violet-600" />
        <div>
          <p className="text-sm font-semibold text-slate-800">Aftercare Chat</p>
          <p className="text-xs text-slate-500">
            Ask questions about your recovery, medications, warning signs, or
            follow-up care.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-3">
            <Heart className="w-10 h-10 opacity-30" />
            <p className="text-sm text-center max-w-xs">
              Ask about your care instructions, what to expect during recovery,
              or when to call your doctor.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                "What activities should I avoid after knee replacement?",
                "When should I call my doctor after surgery?",
                "How do I manage my pain medication schedule?",
              ].map((s) => (
                <button
                  key={s}
                  onClick={() => setQuery(s)}
                  className="text-xs px-3 py-1.5 bg-violet-50 text-violet-700 rounded-full border border-violet-200 hover:bg-violet-100 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                m.role === "user"
                  ? "bg-violet-600 text-white"
                  : "bg-slate-100 text-slate-800"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-xl px-4 py-2.5">
              <Loader2 className="w-4 h-4 animate-spin text-violet-600" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="px-4 py-3 border-t border-slate-100 flex gap-2">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Ask about your care instructions, medications, or recovery…"
          rows={2}
          className="flex-1 resize-none px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
        />
        <button
          onClick={submit}
          disabled={!query.trim() || loading}
          className="self-end px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const PatientAftercarePortal = ({
  facility,
  user,
  systemMessages = [],
  onDismissMessage,
}) => {
  const [tab, setTab] = useState("care-chat");
  const kbRole =
    facility.kb_role ||
    facility.services?.find((s) => s.service_type === "patient_aftercare")
      ?.access_role ||
    "viewer";
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const isAdmin = kbRoleGte("admin");

  const tabs = [
    { id: "care-chat", label: "Care Chat", icon: Heart },
    { id: "knowledge-base", label: "Knowledge Base", icon: FileText },
    { id: "briefings", label: "Briefings", icon: Bell },
    ...(isAdmin
      ? [
          { id: "users", label: "Users", icon: Users },
          { id: "analytics", label: "Analytics", icon: BarChart3 },
          { id: "api-keys", label: "API Keys", icon: Key },
          { id: "prompt-studio", label: "Prompt Studio", icon: Sliders },
          { id: "audit-log", label: "Audit Log", icon: ShieldCheck },
          { id: "customize", label: "Customize", icon: Palette },
        ]
      : []),
  ];

  return (
    <div className="space-y-4">
      {systemMessages.map((msg) => (
        <SystemBanner key={msg.id} message={msg} onDismiss={onDismissMessage} />
      ))}
      <div className="flex space-x-1 bg-white rounded-lg shadow-sm border border-slate-200 p-1 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.id
                ? "bg-violet-600 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <t.icon className="w-4 h-4 mr-1.5" />
            {t.label}
          </button>
        ))}
      </div>
      {tab === "care-chat" && (
        <AftercareChatPanel facility={facility} user={user} />
      )}
      {tab === "knowledge-base" && (
        <KBPanel facility={facility} user={user} kbRole={kbRole} />
      )}
      {tab === "briefings" && <BriefingsPanel facility={facility} />}
      {tab === "users" && isAdmin && <UserManagement facility={facility} />}
      {tab === "analytics" && isAdmin && (
        <UsageAnalyticsDashboard facility={facility} />
      )}
      {tab === "api-keys" && isAdmin && (
        <RAGApiKeys facility={facility} user={user} />
      )}
      {tab === "prompt-studio" && isAdmin && (
        <RAGPromptStudio facility={facility} user={user} />
      )}
      {tab === "audit-log" && isAdmin && <RAGAuditLog facility={facility} />}
      {tab === "customize" && isAdmin && <RAGCustomize facility={facility} />}
    </div>
  );
};

// ── RAG Documents: upload, review, status pipeline ──────────────
